import { useMutation } from '@apollo/client';
import { useAtom, useAtomValue } from 'jotai';

import {
  PAYMENT_GATEWAY,
  TEST_RECORD_PAYMENT_TOKEN,
  TEST_RECORD_SUCCEEDED_STATUS,
} from '../../constants';
import {
  type CountryHaveBank2c2p,
  getSupported2C2PBank,
  type Country,
} from '../../entities/Country';
import type { Currency } from '../../entities/Currency';
import type { Environment, UseCase } from '../../entities/enums';
import { CREATE_2C2P_PAYMENT_INTENT } from '../../graphql';
import { CREATE_2C2P_INVOICE_PAYMENT_INTENT } from '../../graphql/Mutations';
import { useGenerateIdempotencyKey } from '../../hook/useGenerateIdempotencyKey';
import type {
  HirerPaymentResult,
  PaymentStatus,
  Invoice,
  ICreate2C2PPaymentIntentForInvoicePayload,
} from '../../types';
import {
  redirectCartidQuery,
  redirectOnlineBankingQuery,
  redirectOrderidQuery,
} from '../../utils/constants';
import getReturnUrl from '../../utils/getReturnUrl';
import { handleOrderErrorPayload } from '../../utils/handleOrderError';
import { PaymentDefaultError } from '../Errors/customErrors';
import {
  hideIdOnlineBankingAtom,
  hidePhOnlineBankingAtom,
  hideThOnlineBankingAtom,
} from '../Maintenance/MaintenanceStore';

import {
  banksOptionAtom,
  invalidBankAtom,
  selectedBankingOptionAtom,
} from './OnlineBankingStore';

export interface UseOnlineBankingInput<T> {
  useCase: UseCase;
  country: Country;
  currency: Currency;
  env: Environment;
  invoiceInputs: T extends 'INVOICE' ? InvoiceInputs : undefined;
  cajaInputs: T extends 'CAJA' ? CajaInputs : undefined;
}

interface CajaInputs {
  cartId: string;
  referenceId: string; // orderId
}

interface InvoiceInputs {
  customerId: string;
  accountId: string;
  prn: string;
  totalForPaymentIntent: number;
  invoice: Invoice[];
  email: string;
}

const useOnlineBanking = ({
  useCase,
  country,
  currency,
  env,
  invoiceInputs = {
    accountId: '',
    customerId: '',
    prn: '',
    totalForPaymentIntent: 0,
    invoice: [],
    email: '',
  },
  cajaInputs = {
    cartId: '',
    referenceId: '',
  },
}: UseOnlineBankingInput<UseCase>) => {
  const { accountId, customerId, prn, totalForPaymentIntent, invoice, email } =
    invoiceInputs;
  const { cartId, referenceId } = cajaInputs;

  const [create2C2PPaymentIntent] = useMutation(CREATE_2C2P_PAYMENT_INTENT);
  const [create2C2PInvoicePaymentIntent] = useMutation(
    CREATE_2C2P_INVOICE_PAYMENT_INTENT,
  );

  const supportedBanks = getSupported2C2PBank[country as CountryHaveBank2c2p];
  const [banksOption, setBanksOption] = useAtom(banksOptionAtom);
  setBanksOption(supportedBanks);
  const [invalidBank, setInvalidBank] = useAtom(invalidBankAtom);
  const [selectedBankingOption, setSelectedBankingOption] = useAtom(
    selectedBankingOptionAtom,
  );
  const isMaintenanceId = useAtomValue(hideIdOnlineBankingAtom);
  const isMaintenancePh = useAtomValue(hidePhOnlineBankingAtom);
  const isMaintenanceTh = useAtomValue(hideThOnlineBankingAtom);

  const { generateNewIdempotencyKey } = useGenerateIdempotencyKey();

  const handleOnChangeOnlineBanking = (e: any) => {
    generateNewIdempotencyKey();
    if (e.target && e.target.value) {
      setSelectedBankingOption(e.target.value);
    }
  };

  const submitOnlineBankingPayment = async (): Promise<HirerPaymentResult> => {
    // TODO: Tracking

    if (selectedBankingOption === '') {
      setInvalidBank(true);
      return { status: 'requires_action' };
    }

    if (
      (isMaintenanceId && currency === 'IDR') ||
      (isMaintenancePh && currency === 'PHP') ||
      (isMaintenanceTh && currency === 'THB')
    ) {
      throw new PaymentDefaultError('Payment method is in maintenance');
    }

    if (currency !== 'IDR' && currency !== 'PHP' && currency !== 'THB') {
      return { status: 'requires_action' };
    }

    const frontendReturnUrl = getReturnUrl(
      PAYMENT_GATEWAY['2c2p'],
      [
        redirectOnlineBankingQuery,
        redirectCartidQuery(cartId),
        redirectOrderidQuery(referenceId),
      ],
      useCase,
      env,
    );

    const paymentIntent = await create2C2PPaymentIntent({
      variables: {
        input: {
          orderId: referenceId,
          paymentMethodType: 'WEBPAY',
          bank: selectedBankingOption,
          frontendReturnUrl,
        },
      },
    });

    const paymentIntentResponse = paymentIntent.data?.create2C2PPaymentIntent;
    const paymentLink = paymentIntentResponse?.paymentLink;
    const paymentToken = paymentIntentResponse?.paymentToken;

    // Scalability: skip payment gateway call
    if (paymentToken === TEST_RECORD_PAYMENT_TOKEN) {
      return {
        status: 'succeeded' as PaymentStatus,
        paymentIntentId: TEST_RECORD_SUCCEEDED_STATUS,
      };
    }

    const onlineBankingData = {
      paymentLink,
      paymentToken,
    };

    localStorage.setItem(
      'processingPaymentData',
      JSON.stringify(onlineBankingData),
    );

    if (paymentIntentResponse.__typename !== 'PaymentIntent2C2PPayload') {
      handleOrderErrorPayload(
        paymentIntentResponse.__typename,
        paymentIntentResponse.message,
      );
      throw new PaymentDefaultError(paymentIntentResponse.message);
    }

    if (!paymentLink) {
      throw new PaymentDefaultError(paymentIntentResponse?.message);
    }

    window.location.assign(paymentIntentResponse.paymentLink);

    return {
      status: 'succeeded' as PaymentStatus,
    };
  };

  const submitOnlineBankingPaymentForInvoice =
    async (): Promise<HirerPaymentResult> => {
      // TODO: Tracking

      if (selectedBankingOption === '') {
        setInvalidBank(true);
        return { status: 'requires_action' };
      }

      if (
        (isMaintenanceId && currency === 'IDR') ||
        (isMaintenancePh && currency === 'PHP') ||
        (isMaintenanceTh && currency === 'THB')
      ) {
        throw new PaymentDefaultError('Payment method is in maintenance');
      }

      if (currency !== 'IDR' && currency !== 'PHP' && currency !== 'THB') {
        return { status: 'requires_action' };
      }

      const frontendReturnUrl = getReturnUrl(
        PAYMENT_GATEWAY['2c2p'],
        [redirectOnlineBankingQuery],
        useCase,
        env,
      );
      const invoices = invoice.map((i) => ({
        reference: i.id,
        amount: i.amount,
      }));

      const inputVariables: ICreate2C2PPaymentIntentForInvoicePayload = {
        email,
        invoices,
        paymentMethodType: 'WEBPAY',
        prn,
        seekBillingAccountId: customerId, // TODO: need to update graphql to take in customerId instead of seekBillingAccountId
        seekAdvertiserId: accountId, // TODO: need to update graphql to take in accountId instead of seekAdvertiserId
        totalForPaymentIntent,
        frontendReturnUrl,
        bank: selectedBankingOption,
      };

      const paymentIntent = await create2C2PInvoicePaymentIntent({
        variables: {
          input: inputVariables,
        },
      });
      const paymentIntentResponse =
        paymentIntent.data?.create2C2PPaymentIntentForInvoices;
      const paymentLink = paymentIntentResponse?.paymentLink;
      const paymentToken = paymentIntentResponse?.paymentToken;

      // Scalability: skip payment gateway call
      if (paymentToken === TEST_RECORD_PAYMENT_TOKEN) {
        return {
          status: 'succeeded' as PaymentStatus,
          paymentIntentId: TEST_RECORD_SUCCEEDED_STATUS,
        };
      }

      const onlineBankingData = {
        paymentLink,
        paymentToken,
      };

      localStorage.setItem(
        'processingPaymentData',
        JSON.stringify(onlineBankingData),
      );

      if (!paymentLink) {
        throw new PaymentDefaultError(paymentIntentResponse?.message);
      }

      window.location.assign(paymentIntentResponse.paymentLink);

      return {
        status: 'succeeded' as PaymentStatus,
      };
    };

  return {
    banksOption,
    country,
    currency,
    selectedBankingOption,
    invalidBank,
    submitOnlineBankingPayment,
    submitOnlineBankingPaymentForInvoice,
    handleOnChangeOnlineBanking,
  };
};

export default useOnlineBanking;
